/**
 * QuickActions
 *
 * Row of 3 action buttons: Log Period, Log Med, Log Appt.
 * Shared between CalendarView (homepage) and CareScreen.
 */

import { View, Pressable } from "react-native";
import { Text } from "@louise/ui/Text/Text";
import { Droplet, Pill, Calendar } from "lucide-react-native";
import { usePressCapture } from "@src/packages/analytics/usePressCapture";

export interface QuickActionsProps {
  onLogPeriod?: () => void;
  onLogMedication?: () => void;
  onLogAppointment?: () => void;
  /** Whether to show the Log Period button (default: true) */
  showLogPeriod?: boolean;
  /** CareScreen: pink highlight when period selection mode is active */
  periodSelectionActive?: boolean;
}

export function QuickActions({
  onLogPeriod,
  onLogMedication,
  onLogAppointment,
  showLogPeriod = true,
  periodSelectionActive = false,
}: QuickActionsProps) {
  const capturePress = usePressCapture("QuickActions");
  return (
    <View className="flex-row gap-3">
      {/* Log Period */}
      {showLogPeriod && (
        <Pressable
          onPress={() => {
            capturePress("Log Period");
            onLogPeriod?.();
          }}
          disabled={periodSelectionActive}
          className={`flex-1 items-center justify-center gap-1.5 rounded-xl py-2.5 active:scale-95 ${
            periodSelectionActive
              ? "border border-[#F472B6] bg-[#F472B6]/20"
              : "bg-white/5 active:bg-[#F472B6]/10"
          }`}
          accessibilityRole="button"
        >
          <Droplet
            size={20}
            color="#F472B6"
            style={{ filter: "drop-shadow(0 0 8px rgba(244, 114, 182, 0.5))" }}
          />
          <Text className="text-[0.65rem] font-light text-slate-300">
            Log Period
          </Text>
        </Pressable>
      )}

      {/* Log Med */}
      {onLogMedication ? (
        <Pressable
          onPress={() => {
            capturePress("Log Med");
            onLogMedication();
          }}
          className="flex-1 items-center justify-center gap-1.5 rounded-xl bg-white/5 py-2.5 active:scale-95"
          accessibilityRole="button"
        >
          <Pill size={20} color="#A78BFA" />
          <Text className="text-[0.65rem] font-normal text-slate-300">
            Log Med
          </Text>
        </Pressable>
      ) : null}

      {/* Log Appt */}
      {onLogAppointment ? (
        <Pressable
          onPress={() => {
            capturePress("Log Appt");
            onLogAppointment();
          }}
          className="flex-1 items-center justify-center gap-1.5 rounded-xl bg-white/5 py-2.5 active:scale-95"
          accessibilityRole="button"
        >
          <Calendar size={20} color="#2DD4BF" />
          <Text className="text-[0.65rem] font-normal text-slate-300">
            Log Appt
          </Text>
        </Pressable>
      ) : null}
    </View>
  );
}
